// iOS push-notification registration (APNs via @capacitor/push-notifications).
//
// Two entry points, both no-ops off the native iOS shell:
//   initPushIfPermitted() — call on boot/after login. If notification
//     permission is ALREADY granted, silently (re)register to refresh the
//     APNs token and POST it to the backend. Never prompts.
//   promptPushOnMessages() — call the first time the user opens Messages.
//     Prompts for permission ONCE (ever, per device); on grant, registers.
//
// iOS only lets you ask for notification permission once, and a cold
// prompt on first launch tanks opt-in, so we defer the ask to the first
// Messages open (a moment where push obviously matters).
import { Api } from "./api";
import { isCapacitorNative } from "./components";

const _PROMPT_KEY = "cc_push_prompted_v1";
let _listenersWired = false;
let _lastToken = null;

function _plugin() {
  try {
    return (window.Capacitor
      && window.Capacitor.Plugins
      && window.Capacitor.Plugins.PushNotifications) || null;
  } catch (_) {
    return null;
  }
}

function _wireListeners(PN) {
  if (_listenersWired) return;
  _listenersWired = true;
  // 'registration' fires with the APNs device token (token.value).
  PN.addListener("registration", (token) => {
    const value = token && token.value;
    if (!value) return;
    _lastToken = value;
    Api.registerDevice({ token: value, platform: "ios" }).catch(() => {
      // Best-effort: a failed register just means no push until next launch.
    });
  });
  PN.addListener("registrationError", (err) => {
    try { console.warn("push registrationError", err); } catch (_) {}
  });
}

async function _isGranted(PN) {
  try {
    const perm = await PN.checkPermissions();
    return perm && perm.receive === "granted";
  } catch (_) {
    return false;
  }
}

export async function initPushIfPermitted() {
  if (!isCapacitorNative()) return;
  const PN = _plugin();
  if (!PN) return;
  if (await _isGranted(PN)) {
    _wireListeners(PN);
    try { await PN.register(); } catch (_) {}
  }
}

export async function promptPushOnMessages() {
  if (!isCapacitorNative()) return;
  const PN = _plugin();
  if (!PN) return;
  try {
    if (window.localStorage.getItem(_PROMPT_KEY)) return; // asked once already
  } catch (_) { /* storage blocked — fall through and ask */ }

  try {
    let status;
    const perm = await PN.checkPermissions();
    status = perm && perm.receive;
    if (status === "prompt" || status === "prompt-with-rationale") {
      const req = await PN.requestPermissions();
      status = req && req.receive;
    }
    try { window.localStorage.setItem(_PROMPT_KEY, "1"); } catch (_) {}
    if (status === "granted") {
      _wireListeners(PN);
      await PN.register();
    }
  } catch (_) { /* never block the Messages view on a push hiccup */ }
}

// Call on logout so a shared device stops receiving the previous user's
// pushes (the backend also re-points the token on the next user's
// register, but unregistering closes the gap in between).
export async function unregisterPush() {
  if (!_lastToken) return;
  const token = _lastToken;
  _lastToken = null;
  try { await Api.unregisterDevice({ token }); } catch (_) {}
}
